import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import Icon from "@/components/Icon";
import AdminAIConfig from "@/components/admin/AdminAIConfig";
import AdminLayout from "@/components/AdminLayout";

const Stat = ({ label, value, icon }) => (
  <div className="rounded-2xl border border-border bg-card p-4">
    <div className="flex items-center gap-2 text-muted-foreground text-xs font-semibold uppercase tracking-wide"><Icon name={icon} className="h-4 w-4" />{label}</div>
    <div className="text-2xl font-extrabold mt-1">{value}</div>
  </div>
);

export default function AdminMenus() {
  const [tab, setTab] = useState("menus");
  const [overview, setOverview] = useState(null);
  const [imports, setImports] = useState([]);
  const [cfg, setCfg] = useState(null);

  useEffect(() => {
    api.get("/admin/menu/overview").then(({ data }) => setOverview(data)).catch((e) => toast.error(apiError(e.response?.data?.detail)));
    api.get("/admin/menu/imports").then(({ data }) => setImports(data)).catch(() => {});
    loadCfg();
  }, []);

  const [meta, setMeta] = useState(null);
  function loadCfg() { api.get("/admin/menu/config").then(({ data }) => { setCfg(data.config); setMeta(data); }).catch(() => {}); }
  const setFeature = (k, v) => setCfg({ ...cfg, features: { ...cfg.features, [k]: v } });
  const setLimit = (k, v) => setCfg({ ...cfg, limits: { ...cfg.limits, [k]: Number(v) || 0 } });

  return (
    <AdminLayout>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-display font-extrabold flex items-center gap-2"><Icon name="Utensils" className="h-6 w-6 text-primary" />Restaurant Menus</h1>
          <p className="text-sm text-muted-foreground">Oversight of every restaurant menu, AI imports and platform-wide menu settings.</p>
        </div>
      </div>

      <div className="flex gap-1 rounded-xl bg-muted p-1 mb-5 w-fit">
        {[["menus", "Menus"], ["imports", "AI Imports"], ["settings", "Settings"]].map(([id, l]) => (
          <button key={id} onClick={() => setTab(id)} data-testid={`admin-menu-tab-${id}`} className={`px-4 py-1.5 rounded-lg text-sm font-semibold ${tab === id ? "bg-background shadow-sm text-primary" : "text-muted-foreground"}`}>{l}</button>
        ))}
      </div>

      {tab === "menus" && overview && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="Menus" value={overview.totals.menus} icon="Utensils" />
            <Stat label="Restaurants" value={overview.totals.restaurants} icon="Store" />
            <Stat label="Total items" value={overview.totals.items} icon="ListOrdered" />
            <Stat label="AI imports" value={overview.totals.imports} icon="Sparkles" />
          </div>
          <div className="rounded-2xl border border-border bg-card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 text-xs uppercase text-muted-foreground">
                  <tr><th className="text-left p-3">Restaurant / QR</th><th className="text-left p-3">Company</th><th className="p-3">Status</th><th className="p-3">Cats</th><th className="p-3">Items</th><th className="p-3">Tables</th><th className="p-3">Template</th><th className="p-3">Lang</th><th className="p-3">Scans</th></tr>
                </thead>
                <tbody>
                  {overview.menus.map((m) => (
                    <tr key={m.id} className="border-t border-border" data-testid={`admin-menu-row-${m.id}`}>
                      <td className="p-3">
                        <div className="font-semibold">{m.restaurant_name || m.name}</div>
                        <a href={`/p/${m.public_id}`} target="_blank" rel="noreferrer" className="text-xs text-primary">/p/{m.public_id}</a>
                      </td>
                      <td className="p-3">{m.company_name} <span className="text-xs text-muted-foreground">({m.plan || "—"})</span></td>
                      <td className="p-3 text-center"><span className={`text-xs font-bold rounded-full px-2 py-0.5 ${m.status === "active" ? "bg-emerald-500/10 text-emerald-500" : "bg-muted text-muted-foreground"}`}>{m.status}</span></td>
                      <td className="p-3 text-center">{m.categories}</td>
                      <td className="p-3 text-center">{m.items}</td>
                      <td className="p-3 text-center">{m.tables}</td>
                      <td className="p-3 text-center text-xs">{m.template}</td>
                      <td className="p-3 text-center text-xs uppercase">{m.language}</td>
                      <td className="p-3 text-center">{m.scan_count}</td>
                    </tr>
                  ))}
                  {!overview.menus.length && <tr><td colSpan={9} className="p-8 text-center text-muted-foreground">No restaurant menus yet.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
          <p className="text-xs text-muted-foreground flex items-center gap-1.5"><Icon name="Info" className="h-3.5 w-3.5" />Figures reflect engagement (scans, items). Restaurant owners only ever see analytics for their own menus.</p>
        </div>
      )}

      {tab === "imports" && (
        <div className="rounded-2xl border border-border bg-card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-muted/50 text-xs uppercase text-muted-foreground"><tr><th className="text-left p-3">When</th><th className="p-3">Pages</th><th className="p-3">Items</th><th className="p-3">Flagged</th><th className="p-3">Model</th></tr></thead>
            <tbody>
              {imports.map((im) => (
                <tr key={im.id} className="border-t border-border">
                  <td className="p-3">{new Date(im.created_at).toLocaleString()}</td>
                  <td className="p-3 text-center">{im.page_count}</td>
                  <td className="p-3 text-center">{im.item_count}</td>
                  <td className="p-3 text-center">{im.needs_review}</td>
                  <td className="p-3 text-center text-xs">{im.model}</td>
                </tr>
              ))}
              {!imports.length && <tr><td colSpan={5} className="p-8 text-center text-muted-foreground">No AI imports yet.</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {tab === "settings" && cfg && (
        <div className="max-w-4xl space-y-5">
          <div className="rounded-2xl border border-border bg-card p-5">
            <h3 className="font-bold mb-1">Global feature switches</h3><p className="text-xs text-muted-foreground mb-3">Turning a switch off disables it for every company, overriding plans and company overrides. Published menus stay online. Save with "Activate changes" below.</p>
            <div className="space-y-2">
              {[["ai_import", "AI menu import (PDF / image)"], ["ai_describe", "AI dish descriptions"], ["ai_image", "AI image suggestions"], ["cart", "Customer cart & show-to-waiter"], ["tables", "Table QR codes"]].map(([k, l]) => (
                <label key={k} className="flex items-center justify-between py-1.5">
                  <span className="text-sm">{l}</span>
                  <button onClick={() => setFeature(k, !cfg.features[k])} data-testid={`feature-${k}`} className={`h-6 w-11 rounded-full transition-colors relative ${cfg.features[k] ? "bg-primary" : "bg-muted"}`}>
                    <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all ${cfg.features[k] ? "left-[22px]" : "left-0.5"}`} />
                  </button>
                </label>
              ))}
            </div>
          </div>
          <div className="rounded-2xl border border-border bg-card p-5">
            <h3 className="font-bold mb-3">Limits</h3>
            <div className="grid grid-cols-2 gap-3">
              {[["max_categories", "Max categories"], ["max_items_per_category", "Max items / category"], ["max_tables", "Max tables"], ["ai_import_pages", "Max AI import pages"]].map(([k, l]) => (
                <div key={k}>
                  <label className="text-xs font-semibold text-muted-foreground">{l}</label>
                  <input type="number" value={cfg.limits[k]} onChange={(e) => setLimit(k, e.target.value)} data-testid={`limit-${k}`} className="w-full rounded-xl border border-input bg-background px-3.5 py-2.5 text-sm mt-1" />
                </div>
              ))}
            </div>
          </div>
          <div className="rounded-2xl border border-border bg-card p-5">
            <h3 className="font-bold mb-2">Templates & presets available to owners</h3>
            <div className="flex flex-wrap gap-2">
              {cfg.templates.map((t) => <span key={t.id} className="text-xs font-semibold rounded-full bg-muted px-2.5 py-1">{t.name}</span>)}
            </div>
            <div className="flex flex-wrap gap-2 mt-2">
              {cfg.color_presets.map((p) => <span key={p.id} className="text-xs font-semibold rounded-full px-2.5 py-1 flex items-center gap-1.5" style={{ background: `${p.primary}1a`, color: p.primary }}><span className="h-2.5 w-2.5 rounded-full" style={{ background: p.primary }} />{p.name}</span>)}
            </div>
            <p className="text-xs text-muted-foreground mt-3">{cfg.fonts.length} fonts · {cfg.currencies.length} currencies enabled.</p>
          </div>
          {meta && <AdminAIConfig cfg={cfg} setCfg={setCfg} meta={meta} onSaved={loadCfg} />}
        </div>
      )}
    </AdminLayout>
  );
}
